import { useState, useEffect, useCallback } from 'react';
import type { 
  UserProfile, 
  UpdateProfileInput, 
  FriendWithProfile, 
  FriendStats,
  Flight
} from '@/types';
import * as friendService from '@/services/friendService';
import { getFriendFlights } from '@/services/flightService';

interface UseFriendsReturn {
  profile: UserProfile | null;
  friends: FriendWithProfile[];
  pendingRequests: FriendWithProfile[];
  sentRequests: FriendWithProfile[];
  loading: boolean;
  error: string | null;
  updateProfile: (input: UpdateProfileInput) => Promise<void>;
  searchUsers: (email: string) => Promise<UserProfile[]>;
  sendFriendRequest: (friendId: string) => Promise<void>;
  acceptFriendRequest: (friendId: string) => Promise<void>;
  removeFriend: (friendId: string) => Promise<void>;
  getFriendStats: (friendId: string) => Promise<FriendStats>;
  getFriendFlights: (friendId: string) => Promise<Flight[]>;
  refresh: () => Promise<void>;
}

export function useFriends(): UseFriendsReturn {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [friends, setFriends] = useState<FriendWithProfile[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendWithProfile[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    try {
      const userProfile = await friendService.getProfile();
      setProfile(userProfile);
    } catch (err) {
      console.error('Failed to load profile:', err);
      // Only set error if it's not an authentication error
      if (err instanceof Error && !err.message.includes('not authenticated')) {
        setError(err.message);
      }
    }
  }, []);

  const loadFriends = useCallback(async () => {
    try {
      const [acceptedFriends, pending, sent] = await Promise.all([
        friendService.getFriends('accepted'),
        friendService.getFriends('pending'),
        friendService.getSentRequests(),
      ]);
      setFriends(acceptedFriends);
      setPendingRequests(pending);
      setSentRequests(sent);
    } catch (err) {
      console.error('Failed to load friends:', err);
      // Only set error if it's not an authentication error
      if (err instanceof Error && !err.message.includes('not authenticated')) {
        setError(err.message);
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadProfile(), loadFriends()]);
    } finally {
      setLoading(false);
    }
  }, [loadProfile, loadFriends]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateProfileWrapper = useCallback(async (input: UpdateProfileInput) => {
    try {
      const updated = await friendService.updateProfile(input);
      setProfile(updated);
    } catch (err) {
      console.error('Failed to update profile:', err);
      throw err;
    }
  }, []);

  const sendFriendRequestWrapper = useCallback(async (friendId: string) => {
    try {
      await friendService.sendFriendRequest(friendId);
      await loadFriends(); // Refresh friends list
    } catch (err) {
      console.error('Failed to send friend request:', err);
      throw err;
    }
  }, [loadFriends]);

  const acceptFriendRequestWrapper = useCallback(async (friendId: string) => {
    try {
      await friendService.acceptFriendRequest(friendId);
      await loadFriends(); // Refresh to move from pending to accepted
    } catch (err) {
      console.error('Failed to accept friend request:', err);
      throw err;
    }
  }, [loadFriends]);

  const removeFriendWrapper = useCallback(async (friendId: string) => {
    try {
      await friendService.removeFriend(friendId);
      await loadFriends(); // Refresh friends list
    } catch (err) {
      console.error('Failed to remove friend:', err);
      throw err;
    }
  }, [loadFriends]);

  return {
    profile,
    friends,
    pendingRequests,
    sentRequests,
    loading,
    error,
    updateProfile: updateProfileWrapper,
    searchUsers: friendService.searchUsers,
    sendFriendRequest: sendFriendRequestWrapper,
    acceptFriendRequest: acceptFriendRequestWrapper,
    removeFriend: removeFriendWrapper,
    getFriendStats: friendService.getFriendStats,
    getFriendFlights,
    refresh,
  };
}
